import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, Info, CheckCircle, XCircle, Download } from 'lucide-react';
import { useImport } from '@/hooks/useImport';
import { useFinance } from '@/context/FinanceContext';
import { useToast } from '@/components/ui/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExcelJS from 'exceljs';

interface ImportTransactionsButtonProps {
  disabled?: boolean;
}

export const ImportTransactionsButton: React.FC<ImportTransactionsButtonProps> = ({
  disabled = false
}) => {
  const { importing, importTransactions } = useImport();
  const { forceRefresh } = useFinance();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)",
        variant: "destructive"
      });
      return;
    }

    try {
      const result = await importTransactions(file);
      setImportResult(result);

      // Garante que a tela exibe a contagem real do banco logo após o import
      if (result.success) {
        try { await forceRefresh({ force: true }); } catch {}
      }

      toast({
        title: result.success ? "Importação Concluída" : "Erro na Importação",
        description: result.message,
        variant: result.success ? "default" : "destructive"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro inesperado durante a importação",
        variant: "destructive"
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    setImportResult(null);
    fileInputRef.current?.click();
  };

  const generateExampleFile = async () => {
    const headers = [
      "Emissão", "Vencimento", "Pagamento", "Descrição",
      "Valor", "Fornecedor", "Plano de Contas",
      "Conta Financeira", "Forma de Pagamento", "Centro Custo"
    ];

    const exampleRows = [
      ["15/01/2024", "15/01/2024", "15/01/2024", "Venda de produto", 1500.00, "Cliente ABC", "Receita 1", "Conta Corrente", "PIX", "Vendas"],
      ["16/01/2024", "16/02/2024", "", "", -800.00, "Papelaria Central", "Outras despesas administrativas", "Conta Corrente", "Cartão de Crédito", "Administrativo"]
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Lançamentos');

    // Add header row
    worksheet.addRow(headers);

    // Style header row (same as export)
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF475569' }
      };
    });

    // Add data rows
    exampleRows.forEach(row => {
      worksheet.addRow(row);
    });

    // Auto-fit column widths
    worksheet.columns.forEach((column, index) => {
      column.width = 20;
    });

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'exemplo_lancamentos.xlsx';
    link.click();
    
    URL.revokeObjectURL(url);
    
    toast({
      title: "Arquivo de exemplo gerado",
      description: "O arquivo Excel de exemplo foi baixado com sucesso!",
    });
  };

  const requiredColumns = [
    { name: "Emissão", description: "Data de emissão (obrigatório, formato: DD/MM/AAAA)" },
    { name: "Vencimento", description: "Data de vencimento (obrigatório, formato: DD/MM/AAAA)" },
    { name: "Pagamento", description: "Data de pagamento (opcional). Em branco = em aberto, com data = pago" },
    { name: "Descrição", description: "Descrição do lançamento (opcional)" },
    { name: "Valor", description: "Valor em reais - positivo para receitas, negativo para despesas (obrigatório)" },
    { name: "Fornecedor", description: "Nome do fornecedor (obrigatório, deve estar cadastrado no sistema)" },
    { name: "Plano de Contas", description: "Nome do plano de contas (obrigatório, deve estar cadastrado no sistema)" },
    { name: "Conta Financeira", description: "Nome da conta financeira (obrigatório, deve estar cadastrada no sistema)" },
    { name: "Forma de Pagamento", description: "Nome da forma de pagamento (obrigatório, deve estar cadastrada no sistema)" },
    { name: "Centro Custo", description: "Nome do centro de custo (obrigatório, deve estar cadastrado no sistema)" },
  ];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={disabled || importing}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {importing ? 'Importando...' : 'Importar Excel'}
          </Button>
        </DialogTrigger>
        
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Importar Lançamentos do Excel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Selecione um arquivo Excel (.xlsx ou .xls) com os lançamentos para importar. 
                O arquivo deve conter uma linha de cabeçalho seguida pelos dados.
              </AlertDescription>
            </Alert>

            <div>
              <h4 className="text-sm font-medium mb-2">Colunas Necessárias:</h4>
              <ScrollArea className="h-48 border rounded-md p-3">
                <div className="space-y-2">
                  {requiredColumns.map((column, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium text-primary">{column.name}</span>
                      <span className="text-muted-foreground ml-2">- {column.description}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {importResult && (
              <Alert className={importResult.success ? "border-green-500" : "border-red-500"}>
                {importResult.success ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <AlertDescription>
                  {importResult.message}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Erros encontrados:</p>
                      <ScrollArea className="h-24 mt-1">
                        <ul className="text-xs space-y-1">
                          {importResult.errors.map((error: string, index: number) => (
                            <li key={index} className="text-red-600">• {error}</li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={handleButtonClick} 
                disabled={importing}
                className="flex-1"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importando...' : 'Selecionar Arquivo Excel'}
              </Button>
              
              <Button 
                variant="secondary" 
                onClick={generateExampleFile}
                disabled={importing}
              >
                <Download className="h-4 w-4 mr-2" />
                Exemplo
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
